import * as React from "react";
import OverlayLoading from "src/components/common/OverlayLoading/OverlayLoading";
import AuthenticatedUserContext, { IAuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";
import { usersApi } from "../services/users";
import { registerAuthenticationSession, redirectToLogin } from "../helpers/authenticationBridge";
import { setAuthenticationIdentity } from "../helpers/authenticationIdentity";

export const AuthenticatedUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [value, setValue] = React.useState<IAuthenticatedUserContext>();
    React.useEffect(() => {
        let current: IAuthenticatedUserContext | undefined;
        let active = true;
        let cleared = false;
        let loading = false;
        let pending = false;
        const clear = () => {
            cleared = true;
            pending = false;
            current = undefined;
            setValue(undefined);
            setAuthenticationIdentity();
        };
        const refresh = async () => {
            if (!active || cleared) return;
            if (loading) { pending = true; return; }
            loading = true;
            try {
                const response = await usersApi.getUser();
                if (!active || cleared) return;
                if (!response.user) {
                    clear();
                    redirectToLogin();
                    return;
                }
                current = { user: response.user, userSummary: response.userSummary, permissions: response.userSummary?.permissions ?? [] };
                setAuthenticationIdentity(response.user);
                setValue(current);
            } catch (error) {
                if (!active || cleared) return;
                if (!current || (error as { status?: number })?.status === 401) {
                    clear();
                    redirectToLogin();
                }
            } finally {
                loading = false;
                if (active && !cleared && pending) {
                    pending = false;
                    void refresh();
                }
            }
        };
        const unregister = registerAuthenticationSession({ hasUser: () => !!current?.user, refresh: () => { void refresh(); }, clear });
        void refresh();
        return () => { active = false; unregister(); };
    }, []);
    return value ? <AuthenticatedUserContext.Provider value={value}>{children}</AuthenticatedUserContext.Provider> : <OverlayLoading />;
};
